/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  pricingComparison,
  type ComparisonCategory,
} from "@/content/landing/pricing-comparison";

const planNames = ["Mobile", "Starter", "Professional", "Business"] as const;
type PlanKey = "mobile" | "starter" | "professional" | "business";
const planKeys: PlanKey[] = ["mobile", "starter", "professional", "business"];

function CellValue({ value }: { value: string | boolean }) {
  if (value === true) {
    return (
      <Check className="h-4 w-4 text-secondary mx-auto" aria-label="Included" />
    );
  }
  if (value === false) {
    return (
      <Minus
        className="h-4 w-4 text-muted-foreground/40 mx-auto"
        aria-label="Not included"
      />
    );
  }
  return <span className="text-sm text-foreground">{value}</span>;
}

/** Desktop: full 4-column comparison table */
function DesktopTable({ categories }: { categories: ComparisonCategory[] }) {
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="py-3 pr-4 text-sm font-medium text-muted-foreground w-[32%]">
              Feature
            </th>
            {planNames.map((name) => (
              <th
                key={name}
                className={cn(
                  "py-3 px-3 text-sm font-semibold text-center",
                  name === "Starter" ? "text-secondary" : "text-foreground",
                )}
              >
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <>
              <tr key={cat.name}>
                <td
                  colSpan={planKeys.length + 1}
                  className="pt-6 pb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  {cat.name}
                </td>
              </tr>
              {cat.rows.map((row) => (
                <tr
                  key={row.feature}
                  className="border-b border-border/40 hover:bg-muted/20 transition-colors"
                >
                  <td className="py-2.5 pr-4 text-sm text-foreground">
                    {row.feature}
                  </td>
                  {planKeys.map((plan) => (
                    <td key={plan} className="py-2.5 px-3 text-center">
                      <CellValue value={row[plan]} />
                    </td>
                  ))}
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Mobile: one plan at a time with tab switcher */
function MobileTable({ categories }: { categories: ComparisonCategory[] }) {
  const [activePlan, setActivePlan] = useState<PlanKey>("starter");

  return (
    <div className="md:hidden">
      {/* Plan tabs */}
      <div className="flex rounded-xl p-1 bg-muted border border-border mb-4">
        {planKeys.map((plan) => {
          const label =
            plan === "mobile"
              ? "Mobile"
              : plan === "starter"
                ? "Starter"
                : plan === "professional"
                  ? "Pro"
                  : "Business";
          return (
            <button
              key={plan}
              onClick={() => setActivePlan(plan)}
              className={cn(
                "flex-1 rounded-lg py-2 text-xs font-semibold transition-all",
                activePlan === plan
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Feature list for selected plan */}
      <div className="space-y-4">
        {categories.map((cat) => (
          <div key={cat.name}>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              {cat.name}
            </div>
            <div className="rounded-lg border border-border/60 divide-y divide-border/40">
              {cat.rows.map((row) => {
                const value = row[activePlan];
                return (
                  <div
                    key={row.feature}
                    className="flex items-center justify-between px-3 py-2.5"
                  >
                    <span className="text-sm text-foreground">
                      {row.feature}
                    </span>
                    <span className="shrink-0 ml-3">
                      <CellValue value={value} />
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PricingComparisonTable() {
  return (
    <section
      className="py-16 md:py-24 bg-background-warm"
      aria-labelledby="comparison-heading"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
        <div className="text-center mb-10">
          <h2
            id="comparison-heading"
            className="text-2xl md:text-3xl font-bold text-foreground mb-3"
          >
            Compare plans
          </h2>
          <p className="text-muted-foreground">
            Every plan includes the core platform. Here&apos;s what changes as
            you grow.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <DesktopTable categories={pricingComparison} />
          <MobileTable categories={pricingComparison} />
        </div>
      </div>
    </section>
  );
}
